import FVector from '@data/geometry/FVector';
import { GeometryUtils } from '@data/geometry/GeometryUtils';
import { computeOutwardOffsetPointsForEdge } from '@rendering/DesignSchemes/DesignSchemeUtils';
import { ShadingAutomaticRectangleReference } from '@visualizer/types';
import * as THREE from 'three';
import { computeAverageDirectionFromSensors } from '../../SensorDirection';
import { Component, FacadeComponentContext } from './Component';

// =====================================
// TYPES
// =====================================

export type ShadingConfig = {
    depth: number;
    connectCorners: boolean;
    manual: boolean;
    automaticRectangle: boolean;
    automaticRectangleReference: ShadingAutomaticRectangleReference;
    automaticLateralVerticalShade: boolean;
};

export class ShadingComponent extends Component {
    // =====================================
    // CLASS FIELDS
    // =====================================

    private static readonly RAY_EPSILON = 1e-6;
    private readonly depth: number;
    private readonly perimeter: FVector[];
    private readonly edgeIndex: number;
    private readonly renderedIndices: Set<number>;
    private readonly connectCorners: boolean;
    private readonly manual: boolean;
    private readonly automaticRectangle: boolean;
    private readonly automaticRectangleReference: ShadingAutomaticRectangleReference;
    private readonly automaticLateralVerticalShade: boolean;

    constructor(context: FacadeComponentContext, config: ShadingConfig) {
        super(context);
        this.depth = THREE.MathUtils.clamp(config.depth, 0.1, 10);
        this.perimeter = context.perimeter;
        this.edgeIndex = context.edgeIndex;
        this.renderedIndices = context.renderedIndices;
        this.connectCorners = config.connectCorners;
        this.manual = config.manual;
        this.automaticRectangle = config.automaticRectangle;
        this.automaticRectangleReference = config.automaticRectangleReference;
        this.automaticLateralVerticalShade = config.automaticLateralVerticalShade;
    }

    private computeOffsetPointsForEdge(a: FVector, b: FVector): [THREE.Vector2, THREE.Vector2] {
        return computeOutwardOffsetPointsForEdge(this.perimeter, a, b, this.depth);
    }

    // =====================================
    // HELPER FUNCTIONS
    // =====================================

    private getShadingDirection(fallbackStart: THREE.Vector2): FVector | null {
        const edgeDirection = computeAverageDirectionFromSensors(this.sensors);
        const facadeDirection = this.getFacadeDirection();
        const resolvedDirection = !FVector.isZero(edgeDirection) ? edgeDirection : facadeDirection;
        if (!resolvedDirection || FVector.isZero(resolvedDirection)) return null;

        const dir = resolvedDirection.clone().normalize().mult(this.depth);
        const outward = new FVector(fallbackStart.x - this.p1.x, fallbackStart.y - this.p1.y, 0);
        const dirXY = new FVector(dir.x, dir.y, 0);
        if (!FVector.isZero(outward) && !FVector.isZero(dirXY) && FVector.dot(dirXY, outward) < 0) {
            dir.mult(-1);
        }
        return dir;
    }

    private intersectRayWithHorizontalPlane(origin: FVector, dir: FVector, zPlane: number): FVector | null {
        if (Math.abs(dir.z) < ShadingComponent.RAY_EPSILON) return null;
        const t = (zPlane - origin.z) / dir.z;
        if (!Number.isFinite(t) || t < 0) return null;
        return new FVector(origin.x + t * dir.x, origin.y + t * dir.y, zPlane);
    }

    private isFinitePoint(p: FVector | null): p is FVector {
        return !!p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);
    }

    private resolvePerpendicularOffsetDirection(p1Top: FVector, p2Top: FVector, p1Target: FVector, p2Target: FVector): FVector | null {
        const edge = p2Top.clone().sub(p1Top);
        const edgeXY = new FVector(edge.x, edge.y, 0);
        const edgeLen = edgeXY.mag();
        if (edgeLen <= ShadingComponent.RAY_EPSILON) return null;

        const perp = new FVector(-edgeXY.y / edgeLen, edgeXY.x / edgeLen, 0);
        const topCenter = p1Top.clone().add(p2Top).mult(0.5);
        const targetCenter = p1Target.clone().add(p2Target).mult(0.5);
        const centerDir = targetCenter.sub(topCenter);
        const centerDirXY = new FVector(centerDir.x, centerDir.y, 0);

        if (FVector.isZero(centerDirXY)) return perp;
        return FVector.dot(perp, centerDirXY) >= 0 ? perp : perp.mult(-1);
    }

    // =====================================
    // MAIN GENERATION FUNCTION
    // =====================================

    private addLateralVerticalShadeFromSunProjection(
        group: THREE.Group,
        direction: FVector | null,
        p1Top: FVector,
        p2Top: FVector,
        p1Target: FVector,
        p2Target: FVector
    ): void {
        // 1) Get inverse sun direction in 3D (towards ground):
        if (!direction) return;
        const invSunDir = direction.clone().mult(-1);
        if (FVector.isZero(invSunDir)) return;
        const sunXY = new THREE.Vector2(invSunDir.x, invSunDir.y);
        if (sunXY.lengthSq() <= ShadingComponent.RAY_EPSILON) return;
        const originalSunXY = new THREE.Vector2(direction.x, direction.y);
        if (originalSunXY.lengthSq() <= ShadingComponent.RAY_EPSILON) return;

        // 2) Pick side in XY (Top1Offset vs Top2Offset side):
        const zBottom = this.p1.z;
        const p1Bottom = new FVector(this.p1.x, this.p1.y, zBottom);
        const p2Bottom = new FVector(this.p2.x, this.p2.y, zBottom);
        const edge2D = new THREE.Vector2(p2Top.x - p1Top.x, p2Top.y - p1Top.y);
        const useP2Side = edge2D.dot(originalSunXY) >= 0;
        const sideTopA = useP2Side ? p2Top : p1Top;
        const sideTopB = useP2Side ? p2Target : p1Target;
        const sideBottom = useP2Side ? p2Bottom : p1Bottom;

        // 3) Build a simple lateral face by directly connecting the chosen top offset side to its bottom facade point.
        const geom = new THREE.BufferGeometry().setFromPoints(
            [sideTopA, sideTopB, sideBottom].map((p) => new THREE.Vector3(p.x, p.y, p.z))
        );
        geom.setIndex([0, 1, 2]);
        geom.computeVertexNormals();
        group.add(new THREE.Mesh(geom, this.material));
        group.add(new THREE.LineSegments(new THREE.EdgesGeometry(geom, 0.01), ShadingComponent.edgeMaterial));
    }

    override build(): THREE.Group {
        const group = new THREE.Group();

        // 1) Skip edges without a transparent facade:
        if (!this.renderedIndices.has(this.edgeIndex)) return group;

        // 2) Compute outward offset points for the current edge:
        let [p1Out, p2Out] = this.computeOffsetPointsForEdge(this.p1, this.p2);

        // 3) Build top facade edge and fallback points in the slab-bottom plane:
        const z = this.p1.z + this.height;
        let p1Top = new FVector(this.p1.x, this.p1.y, z);
        let p2Top = new FVector(this.p2.x, this.p2.y, z);
        const p1OutTop = new FVector(p1Out.x, p1Out.y, z);
        const p2OutTop = new FVector(p2Out.x, p2Out.y, z);

        // 4) Automatically compute the shading from the component direction:
        const direction = this.getShadingDirection(p1Out);

        // 4.1) Intersect rays from bottom edge points with the top slab-bottom plane (z):
        const p1Intersection = direction ? this.intersectRayWithHorizontalPlane(this.p1, direction, z) : null;
        const p2Intersection = direction ? this.intersectRayWithHorizontalPlane(this.p2, direction, z) : null;

        // 4.2) Check if ray intersections are valid (they must be inside the p1Top-p2Top Offsetted segment):
        const useRayTargets = this.isFinitePoint(p1Intersection) && this.isFinitePoint(p2Intersection);
        let p1Target = p1OutTop;
        let p2Target = p2OutTop;
        if (useRayTargets && !this.manual) {
            // P1 and P2 projected on the P1Intersected and P2Interesected line:
            const p1Projected2D = GeometryUtils.projectPointToInfiniteLine2D(
                new THREE.Vector2(p1Top.x, p1Top.y),
                new THREE.Vector2(p1Intersection.x, p1Intersection.y),
                new THREE.Vector2(p2Intersection.x, p2Intersection.y)
            );
            const p2Projected2D = GeometryUtils.projectPointToInfiniteLine2D(
                new THREE.Vector2(p2Top.x, p2Top.y),
                new THREE.Vector2(p1Intersection.x, p1Intersection.y),
                new THREE.Vector2(p2Intersection.x, p2Intersection.y)
            );
            const p1TopOffsetted = new FVector(p1Projected2D.x, p1Projected2D.y, z);
            const p2TopOffsetted = new FVector(p2Projected2D.x, p2Projected2D.y, z);

            // Check if P1Intersected and P2Intersected lie on the P1 and P2 projected segment:
            const p1OnOffsetSegment = GeometryUtils.isPointOnSegment2D(
                new THREE.Vector2(p1Intersection.x, p1Intersection.y),
                new THREE.Vector2(p1TopOffsetted.x, p1TopOffsetted.y),
                new THREE.Vector2(p2TopOffsetted.x, p2TopOffsetted.y),
                ShadingComponent.RAY_EPSILON
            );
            const p2OnOffsetSegment = GeometryUtils.isPointOnSegment2D(
                new THREE.Vector2(p2Intersection.x, p2Intersection.y),
                new THREE.Vector2(p1TopOffsetted.x, p1TopOffsetted.y),
                new THREE.Vector2(p2TopOffsetted.x, p2TopOffsetted.y),
                ShadingComponent.RAY_EPSILON
            );

            // If the points lie on the segment, keep them, else use the projected P1 and P2:
            p1Target = p1OnOffsetSegment ? p1Intersection : p1TopOffsetted;
            p2Target = p2OnOffsetSegment ? p2Intersection : p2TopOffsetted;
        }

        // 5) Connect shading corners:
        if (this.connectCorners && this.manual) {
            const idx = this.edgeIndex;
            const nextIdx = (idx + 1) % this.perimeter.length;
            const prevIdx = (idx - 1 + this.perimeter.length) % this.perimeter.length;

            const prevConnectable = this.renderedIndices.has(prevIdx);
            const nextConnectable = this.renderedIndices.has(nextIdx);

            const currLineA = new THREE.Vector2(p1Target.x, p1Target.y);
            const currLineB = new THREE.Vector2(p2Target.x, p2Target.y);

            // 5.1) Merge with previous edge by intersecting lines:
            if (prevConnectable) {
                const prevA = this.perimeter[prevIdx];
                const prevB = this.perimeter[idx];
                const [prevOutA, prevOutB] = this.computeOffsetPointsForEdge(prevA, prevB);
                const interPrev = GeometryUtils.intersect2D(prevOutA, prevOutB, currLineA, currLineB);
                if (interPrev) p1Target = new FVector(interPrev.x, interPrev.y, z);
            }

            // 5.2) Merge with next edge by intersecting lines:
            if (nextConnectable) {
                const nextA = this.perimeter[nextIdx];
                const nextB = this.perimeter[(nextIdx + 1) % this.perimeter.length];
                const [nextOutA, nextOutB] = this.computeOffsetPointsForEdge(nextA, nextB);
                const interNext = GeometryUtils.intersect2D(currLineA, currLineB, nextOutA, nextOutB);
                if (interNext) p2Target = new FVector(interNext.x, interNext.y, z);
            }
        }

        // 6) In Automatic mode make shading rectangle by following either the longer or shorted trepezium edge:
        if (!this.manual && this.automaticRectangle) {
            const perp = this.resolvePerpendicularOffsetDirection(p1Top, p2Top, p1Target, p2Target);
            if (perp) {
                const d1 = Math.abs(FVector.dot(p1Target.clone().sub(p1Top), perp));
                const d2 = Math.abs(FVector.dot(p2Target.clone().sub(p2Top), perp));
                const offset = (d1 + d2) / 2;
                const shift = perp.mult(offset);

                if (this.automaticRectangleReference === ShadingAutomaticRectangleReference.LONGER) {
                    p1Target = p1Top.clone().add(shift);
                    p2Target = p2Top.clone().add(shift);
                } else if (this.automaticRectangleReference === ShadingAutomaticRectangleReference.SHORTER) {
                    p1Top = p1Target.clone().sub(shift);
                    p2Top = p2Target.clone().sub(shift);
                }
            }
        }

        // 7) Build/render the final shading quad:
        const topGeom = new THREE.BufferGeometry().setFromPoints(
            [p1Top, p2Top, p2Target, p1Target].map((p) => new THREE.Vector3(p.x, p.y, p.z))
        );
        topGeom.setIndex([0, 1, 2, 0, 2, 3]);
        topGeom.computeVertexNormals();

        const topMesh = new THREE.Mesh(topGeom, this.material);
        const topEdges = new THREE.LineSegments(new THREE.EdgesGeometry(topGeom, 0.01), ShadingComponent.edgeMaterial);
        group.add(topMesh, topEdges);

        // 8) In Automatic mode, optionally add a lateral vertical shade connecting the facade edge to the sun projection on the ground:
        if (!this.manual && this.automaticLateralVerticalShade) {
            this.addLateralVerticalShadeFromSunProjection(group, direction, p1Top, p2Top, p1Target, p2Target);
        }

        // Done:
        return group;
    }
}
